import { countClientsNeedingCohortReview } from '../../domain/services/cohortAssignmentReviewService';
import {
  getAssignedClientsCountByCohort,
  getClientIdsAssignedToCohort,
  getCohortFilterLabels,
  normalizeCohort,
  reindexCohorts,
  removeCohort,
  sortCohortsByOrder,
  upsertCohort,
} from '../../domain/services/cohortService';

/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CareModelCohort
 * } CareModelCohort */
/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').Client
 * } Client */
/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CohortFilters
 * } CohortFilters */
/** @typedef {
 *   import('./createCareModelCohorts.types').LoadCareModelCohortsParams
 * } LoadCareModelCohortsParams */
/** @typedef {
 *   import('./createCareModelCohorts.types').SaveCareModelCohortParams
 * } SaveCareModelCohortParams */
/** @typedef {
 *   import('./createCareModelCohorts.types').DeleteCareModelCohortParams
 * } DeleteCareModelCohortParams */
/** @typedef {
 *   import('./createCareModelCohorts.types').DeleteCareModelCohortResult
 * } DeleteCareModelCohortResult */
/** @typedef {
 *   import('./createCareModelCohorts.types').ReorderCareModelCohortsParams
 * } ReorderCareModelCohortsParams */
/** @typedef {
 *   import('./createCareModelCohorts.types').ClientsNeedingCohortReviewParams
 * } ClientsNeedingCohortReviewParams */
/** @typedef {
 *   import('./createCareModelCohorts.types').CareModelCohortsUseCase
 * } CareModelCohortsUseCase */

/**
 * @param {Function} saveCareModelCohortsApi
 * @param {Function} generateId
 * @param {Function} serializeFilters
 * @param {Function} unassignClientsFromCohortApi
 * @returns {CareModelCohortsUseCase}
 */
// eslint-disable-next-line import/prefer-default-export
export function createCareModelCohorts(
  saveCareModelCohortsApi,
  generateId,
  serializeFilters,
  unassignClientsFromCohortApi,
) {
  /**
   * @param {LoadCareModelCohortsParams} params
   * @returns {CareModelCohort[]}
   */
  function loadCohorts({ storedCohorts = [] }) {
    return sortCohortsByOrder(storedCohorts);
  }

  /**
   * @param {SaveCareModelCohortParams} params
   * @returns {Promise<CareModelCohort[]>}
   */
  async function saveCohort({ clinicId, currentCohorts = [], cohort }) {
    const normalizedCohort = normalizeCohort(
      cohort,
      generateId(),
      currentCohorts.length,
    );

    const preparedCohort = {
      ...normalizedCohort,
      filters: serializeFilters(normalizedCohort.filters || {}),
    };

    const nextCohorts = reindexCohorts(
      upsertCohort(currentCohorts, preparedCohort),
    );

    await saveCareModelCohortsApi({
      clinicId,
      careModelCohorts: nextCohorts,
    });

    return nextCohorts;
  }

  /**
   * @param {DeleteCareModelCohortParams} params
   * @returns {Promise<DeleteCareModelCohortResult>}
   */
  async function deleteCohort({ clinicId, currentCohorts = [], cohortId, clients = [] }) {
    const unassignedClientIds = getClientIdsAssignedToCohort(cohortId, clients);

    // Unassign before removing the cohort: a failed unassign then leaves the cohort in place, so
    // the clients still point at something that exists and the whole action stays retryable.
    await unassignClientsFromCohortApi({ clientIds: unassignedClientIds });

    const nextCohorts = reindexCohorts(
      removeCohort(currentCohorts, cohortId),
    );

    await saveCareModelCohortsApi({
      clinicId,
      careModelCohorts: nextCohorts,
    });

    return { cohorts: nextCohorts, unassignedClientIds };
  }

  /**
   * @param {ReorderCareModelCohortsParams} params
   * @returns {Promise<CareModelCohort[]>}
   */
  async function reorderCohorts({ clinicId, cohorts = [] }) {
    const nextCohorts = reindexCohorts(cohorts);

    await saveCareModelCohortsApi({
      clinicId,
      careModelCohorts: nextCohorts,
    });

    return nextCohorts;
  }

  /**
   * @param {Client[]} clients
   * @returns {Record<string, number>}
   */
  function getAssignedClientsCount(clients = []) {
    return getAssignedClientsCountByCohort(clients);
  }

  /**
   * @param {ClientsNeedingCohortReviewParams} params
   * @returns {number}
   */
  function getClientsNeedingCohortReviewCount({ clients = [], impactedClients = [] }) {
    return countClientsNeedingCohortReview({ clients, impactedClients });
  }

  /**
   * @param {CohortFilters} filters
   * @returns {string[]}
   */
  function getFilterLabels(filters = {}) {
    return getCohortFilterLabels(filters);
  }

  return {
    loadCohorts,
    saveCohort,
    deleteCohort,
    reorderCohorts,
    getAssignedClientsCount,
    getClientsNeedingCohortReviewCount,
    getFilterLabels,
  };
}
